import { Translate } from '@teliaee/telia-translate';
import axios, { AxiosError } from 'axios';
import { format } from 'date-fns';
import { action, computed, observable, runInAction } from 'mobx';
import { DatepickerValue, IDateRange, IFormHelperProps, ISelectOption, TSelectValue } from 'telia-front-react';
import ErrorUtil from '../error/ErrorUtil';
import {
  AvailableReservation,
  Branch,
  BranchType,
  BranchWithChild,
  CitiesHolder,
  ErrorType,
  Reservation,
  ReservationCreation,
  ReservationCreationError,
  ReservationCreationState
} from '../reservation/common/ReservationInterfaces';
import API from '../util/ApiUtil';
import { FetchStatus } from '../util/FetchStatus';
import {
  BRANCH_FIELD_ID,
  countryCallingCodeFieldHelper,
  DATE_AND_TIME_ID,
  emailFieldHelper,
  isEmailValid,
  isPhoneValid,
  notEmptyFieldHelper,
  phoneFieldHelper
} from '../util/FieldHelperUtil';
import { MAX_NAME_FIELD_LENGTH } from './CallbackStore';
import ContactInfoStore from './ContactInfoStore';
import CountryCallingCodeStore, { removePlusAndEstonianAreaCode } from './CountryCallingCodeStore';
import LanguageStore from './LanguageStore';
import RootStore from './RootStore';
import RouteStore from './RouteStore';
import SsoStore, { CustomerSegmentType } from './SsoStore';

class ReservationStore {
  @observable accessor branchList: Branch[] = [];
  @observable accessor cities: CitiesHolder = { cities: [], status: FetchStatus.UNINITIALIZED };
  @observable accessor encryptedReservationId: string;
  @observable accessor reservation: Reservation;
  @observable accessor reservationStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor reservationCancelStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor reservationUseStatus: FetchStatus = FetchStatus.UNINITIALIZED;
  @observable accessor errorType: ErrorType;
  @observable accessor timesLoaded: boolean = false;
  @observable accessor availableReservations: AvailableReservation[] = [];
  @observable accessor reservationCreation: ReservationCreation = {
    phone: '',
    email: '',
    time: null,
    language: null,
    description: '',
    subjectContext: 'B2C'
  };
  @observable accessor reservationCreationState: ReservationCreationState = {
    status: 'BEING_FILLED',
    errors: []
  };
  @observable accessor selectedBranch: ISelectOption = null;
  @observable accessor requireTechnician: boolean = false;
  @observable accessor hasReservationCreationFormSubmitted: boolean = false;

  private routeStore: RouteStore;
  private languageStore: LanguageStore;
  private ssoStore: SsoStore;
  private contactInfoStore: ContactInfoStore;
  private countryCallingCodeStore: CountryCallingCodeStore;

  public constructor(rootStore: RootStore) {
    this.routeStore = rootStore.routeStore;
    this.languageStore = rootStore.languageStore;
    this.ssoStore = rootStore.ssoStore;
    this.contactInfoStore = rootStore.contactInfoStore;
    this.countryCallingCodeStore = rootStore.countryCallingCodeStore;

    this.reservationCreation.language = this.languageStore.currentLanguage;
  }

  @computed
  public get isReservationFetching(): boolean {
    return [FetchStatus.UNINITIALIZED, FetchStatus.FETCHING].includes(this.reservationStatus);
  }

  @computed
  public get branchHasTechChild(): boolean {
    return this.branchHasChildOfType(BranchType.TECH_STORE);
  }

  @computed
  public get branchHasB2BChild(): boolean {
    return this.branchHasChildOfType(BranchType.B2B_STORE);
  }

  @computed
  public get requiredFieldsAreValid(): boolean {
    return (
      this.reservationCreation.time &&
      (this.countryCallingCodeStore.isForeignCodeSelected
        ? this.reservationCreation.email && isEmailValid(this.reservationCreation.email)
        : this.reservationCreation.phone && isPhoneValid(this.reservationCreation.phone.replace('+', '')))
    );
  }

  @computed
  public get selectedBranchName(): string {
    return this.findBranchById(+this.selectedBranch.value).name;
  }

  public fetchBranchList = async () => {
    const response = await API.get('/reservation/branches');
    runInAction(() => {
      this.branchList = response.data;
    });
  };

  @action.bound
  public async fetchCities() {
    this.cities.status = FetchStatus.FETCHING;
    try {
      const { data } = await API.get('reservation/cities');
      runInAction(() => {
        this.cities.cities = data;
        this.cities.status = FetchStatus.FETCHED;
      });
    } catch (e) {
      runInAction(() => {
        this.cities.status = FetchStatus.ERROR;
        ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      });
    }
  }

  @action.bound
  public async fetchAvailableReservations() {
    this.reservationCreation.time = null;
    this.timesLoaded = false;
    const branchId = this.getSelectedBranchIdConsideringChildStores();

    if (!branchId) {
      return;
    }
    try {
      const { data } = await API.get('/reservation/available', { params: { branchId } });
      runInAction(() => {
        this.availableReservations = data;
        this.timesLoaded = true;
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
    }
  }

  @action.bound
  public async useReservation(reservation: Reservation): Promise<void> {
    try {
      this.reservationUseStatus = FetchStatus.FETCHING;
      const response = await API.put(`reservation/${this.encryptedReservationId}/use`);
      runInAction(() => {
        reservation.ticketNumber = response.data;
        reservation.stateCode = 'USED';
        this.reservationUseStatus = FetchStatus.FETCHED;
      });
    } catch {
      runInAction(() => (this.reservationUseStatus = FetchStatus.ERROR));
    }
  }

  @action.bound
  public fetchReservation = async () => {
    if (this.reservationStatus === FetchStatus.ERROR || this.reservation) {
      return;
    }
    try {
      this.reservationStatus = FetchStatus.FETCHING;
      this.decodeIdFromParameter();

      if (!this.encryptedReservationId) {
        return;
      }
      const { data } = await API.get(`reservation/${this.encryptedReservationId}`);
      runInAction(() => {
        this.reservation = data;
        this.reservationStatus = FetchStatus.FETCHED;
        if (data.stateCode) {
          this.setReservationStateError(data.stateCode);
        }
      });
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      if (axios.isAxiosError(e)) {
        const axiosError: AxiosError = e;
        if (axiosError.code === '400') {
          this.setReservationError('INVALID_ID');
          return;
        }
      }
      this.setReservationError('REQUEST');
    }
  };

  @action.bound
  public async createReservation() {
    const isForeignCodeSelected = this.countryCallingCodeStore.isForeignCodeSelected;
    try {
      this.reservationCreationState.status = 'SENDING';
      const updatedReservation: ReservationCreation = {
        ...this.reservationCreation,
        phone: isForeignCodeSelected ? '' : this.reservationCreation.phone,
        email: isForeignCodeSelected ? this.reservationCreation.email : '',
        customerId: this.ssoStore.customerId || null,
        name: this.ssoStore.customer?.displayName?.slice(0, MAX_NAME_FIELD_LENGTH) || '',
        subjectContext: this.branchHasB2BChild ? this.reservationCreation.subjectContext : null
      };
      const branchId = this.getSelectedBranchIdConsideringChildStores();
      await API.post(`/reservation/create/${branchId}`, updatedReservation);
      runInAction(() => (this.reservationCreationState.status = 'SUCCESS'));
    } catch (e) {
      await this.handleReservationCreationError(e as AxiosError);
    }
  }

  @action.bound
  public async cancelReservation() {
    if (!this.encryptedReservationId) {
      return;
    }
    try {
      this.reservationCancelStatus = FetchStatus.FETCHING;
      await API.put(`reservation/${this.encryptedReservationId}/cancel`);
      runInAction(() => (this.reservationCancelStatus = FetchStatus.FETCHED));
    } catch (e) {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      runInAction(() => (this.reservationCancelStatus = FetchStatus.ERROR));
    }
  }

  public isBranchEnabled = (branchId: number) => {
    const branch = this.branchList.find((branchesItem) => branchesItem.branchId === branchId);
    return branch?.reservationsEnabled;
  };

  public findChildrenByParentId = (id: number): Branch[] => {
    return id && this.branchList.filter((b: Branch) => b.parentBranchId === id);
  };

  public findBranchById = (id: number): Branch => {
    return id ? this.branchList.find((b: Branch) => b.branchId === id) : null;
  };

  public findBranchAndChildrenWhenExist = (branch: Branch): BranchWithChild => {
    const isParent = !branch.parentBranchId;

    return {
      parent: isParent ? branch : this.findBranchById(branch.parentBranchId),
      children: isParent ? this.findChildrenByParentId(branch.branchId) : [branch]
    };
  };

  @action.bound
  public async setInitialValuesOfBranchAndTimeAndAvailableReservations(translate: Translate): Promise<void> {
    const { branchId, time } = this.routeStore.decodedParams;

    if (!branchId) {
      return;
    }
    const { parent, children } = this.findBranchAndChildrenWhenExistByBranchId(+branchId);

    if (!parent) {
      return Promise.resolve();
    }

    if (this.findBranchWithTypeAndId(children, +branchId, BranchType.TECH_STORE)) {
      this.requireTechnician = true;
    }
    if (this.findBranchWithTypeAndId(children, +branchId, BranchType.B2B_STORE)) {
      this.reservationCreation.subjectContext = 'B2B';
    }

    this.selectedBranch = this.branchToOption(parent, translate);
    await this.fetchAvailableReservations();
    this.setInitialQueryParamTimeIfIsAvailable(time);
  }

  @action.bound
  public async selectBranchAndFetchTimes(choice: TSelectValue): Promise<void> {
    this.selectedBranch = choice as ISelectOption;
    this.requireTechnician = false;

    await this.fetchAvailableReservations();
  }

  public getBranchOptions(translate: Translate): ISelectOption[] {
    return this.branchList
      .filter((b: Branch) => b.type === BranchType.STORE && b.reservationsEnabled)
      .map((branch: Branch) => this.branchToOption(branch, translate))
      .sort((br1: ISelectOption, br2: ISelectOption) => br1.label.localeCompare(br2.label));
  }

  @action.bound
  public selectTime(time: DatepickerValue): void {
    this.reservationCreation.time = time ? format((time as IDateRange).from, "yyyy-MM-dd'T'HH:mm") : null;
  }

  @action.bound
  public setPhone(phone: string): void {
    this.reservationCreation.phone = phone;
  }

  @action.bound
  public async setRequireTechnician(): Promise<void> {
    this.requireTechnician = !this.requireTechnician;

    await this.fetchAvailableReservations();
  }

  @action.bound
  public changeCustomerType(customerType: CustomerSegmentType) {
    this.reservationCreation.subjectContext = customerType;
    if (this.selectedBranch) {
      this.fetchAvailableReservations();
    }
  }

  public prefillReservationCreation = async () => {
    const customerId = this.ssoStore.customerId;
    await this.contactInfoStore.fetchContactInfo(customerId);

    runInAction(
      () =>
        (this.reservationCreation = {
          ...this.reservationCreation,
          phone: this.contactInfoStore.phone ? removePlusAndEstonianAreaCode(this.contactInfoStore.phone) : null,
          email: this.contactInfoStore.email || '',
          customerId,
          name: this.ssoStore.customer.displayName?.slice(0, MAX_NAME_FIELD_LENGTH) || '',
          subjectContext: this.branchHasB2BChild ? this.reservationCreation.subjectContext : null
        })
    );
  };

  @action.bound
  public validateFieldsAndShowPreview(): void {
    this.hasReservationCreationFormSubmitted = true;
    this.reservationCreationState.status = this.requiredFieldsAreFilled ? 'PREVIEW' : 'VALIDATION_FAILED';
  }

  @computed
  private get requiredFieldsAreFilled(): boolean {
    return !!this.selectedBranch && !!this.reservationCreation.time && this.requiredFieldsAreValid;
  }

  @action.bound
  private decodeIdFromParameter() {
    try {
      this.encryptedReservationId = this.routeStore.decodedParams.reservationId;
      if (!this.encryptedReservationId) {
        this.setReservationError('INVALID_ID');
      }
    } catch (_e) {
      this.setReservationError('INVALID_ID');
    }
  }

  @action.bound
  private setReservationError(errorType: ErrorType) {
    this.reservationStatus = FetchStatus.ERROR;
    this.errorType = errorType;
  }

  private setReservationStateError = (stateCode: string) => {
    switch (stateCode) {
      case 'CANCELLED':
      case 'USED':
        this.setReservationError('INVALID_ID');
        return;
      default:
        return;
    }
  };

  @action.bound
  private setInitialQueryParamTimeIfIsAvailable(time: string) {
    if (this.availableReservations.some((ar: AvailableReservation) => ar.time === time)) {
      this.reservationCreation.time = time;
    }
  }

  @action.bound
  private handleReservationCreationError(e: AxiosError): void {
    if (e.response?.status === ReservationCreationError.BAD_REQUEST) {
      this.reservationCreationState.status = 'INVALID_INPUT';
      this.reservationCreationState.errors = (e.response.data as any).errors;
      this.fetchAvailableReservations();
    } else {
      ErrorUtil.pushErrorToTeliaRumAndLog(e as Error);
      this.reservationCreationState.status = 'ERROR';
    }
  }

  private findBranchAndChildrenWhenExistByBranchId = (branchId: number): BranchWithChild => {
    const branch = this.findBranchById(branchId);

    if (!branch) {
      return { parent: null, children: null };
    }

    const isParent = !branch.parentBranchId;

    return {
      parent: isParent ? branch : this.findBranchById(branch.parentBranchId),
      children: isParent ? this.findChildrenByParentId(branch.branchId) : [branch]
    };
  };

  private findEnabledChildrenByParentId(id: number): Branch[] {
    return id ? this.branchList.filter((b: Branch) => b.parentBranchId === id && b.reservationsEnabled) : null;
  }

  private findChildrenByBranch(branch: Branch): BranchWithChild {
    const isParent = !branch.parentBranchId;

    return {
      parent: isParent ? branch : this.findBranchById(branch.parentBranchId),
      children: isParent && this.findEnabledChildrenByParentId(branch.branchId)
    };
  }

  private findBranchWithTypeAndId = (branches: Branch[], id: number, type: BranchType): Branch => {
    return branches?.find((branch) => branch.branchId === id && branch.type === type);
  };

  private getSelectedBranchIdConsideringChildStores(): number {
    const { parent, children } = this.findBranchAndChildrenWhenExistByBranchId(+this.selectedBranch.value);
    const techChild = children?.find((child) => child.type === BranchType.TECH_STORE);
    const B2BChild = children?.find((child) => child.type === BranchType.B2B_STORE);

    if (this.requireTechnician) {
      return techChild.branchId;
    }

    return this.reservationCreation.subjectContext === 'B2B' ? B2BChild.branchId : parent.branchId;
  }

  private branchHasChildOfType(type: BranchType): boolean {
    if (!this.selectedBranch) {
      return false;
    }
    const { children } = this.findBranchAndChildrenWhenExistByBranchId(+this.selectedBranch.value);
    return !!children?.find((child) => child.type === type);
  }

  private branchToOption(branch: Branch, translate: Translate): ISelectOption {
    return {
      value: `${branch.branchId}`,
      label: this.buildBranchName(branch, translate)
    };
  }

  private buildBranchName(branch: Branch, translate: Translate): string {
    const { children } = this.findChildrenByBranch(branch);
    let branchName = branch.name;

    if (branch.city?.cityName) {
      branchName = `${translate(`reservation.city.${branch.city.cityName}`)}, ${branchName}`;
    }

    if (children?.length) {
      const values = [];
      if (children?.find((child) => child.type === BranchType.TECH_STORE)) {
        values.push(translate('reservation.branch.technicalSupport'));
      }
      if (children?.find((child) => child.type === BranchType.B2B_STORE)) {
        values.push(translate('reservation.branch.b2bConsultant'));
      }
      branchName += ` (${values.join(', ')})`;
    }

    return branchName;
  }

  public getPhoneHelper = (translate: Translate): IFormHelperProps =>
    phoneFieldHelper(translate, this.reservationCreation.phone, this.hasReservationCreationFormSubmitted);

  public getEmailHelper = (translate: Translate): IFormHelperProps =>
    emailFieldHelper(translate, this.reservationCreation.email, this.hasReservationCreationFormSubmitted);

  public getCallingCodeHelper = (translate: Translate): IFormHelperProps =>
    countryCallingCodeFieldHelper(translate, this.countryCallingCodeStore.countryCallingCode, this.hasReservationCreationFormSubmitted);

  public getDateAndTimeHelper = (translate: Translate): IFormHelperProps =>
    notEmptyFieldHelper(translate, this.reservationCreation.time, this.hasReservationCreationFormSubmitted, DATE_AND_TIME_ID);

  public getBranchHelper = (translate: Translate): IFormHelperProps =>
    notEmptyFieldHelper(translate, this.selectedBranch?.value, this.hasReservationCreationFormSubmitted, BRANCH_FIELD_ID);
}

export default ReservationStore;
